import {
    EmissionsConfiguration,
    OutboundMessagingConfiguration,
} from "models/Emissions";
import { useCallback, useEffect, useMemo, useState } from "react";
import EmissionsService from "services/EmissionsService";

export const getBodyEndpointId = (body: EmissionsConfiguration) =>
    body.configuration?.endPoints?.[0]?.integrationId;

export const bodyUsesEndpoint = (
    body: EmissionsConfiguration,
    endpointId: string,
) =>
    (body.configuration?.endPoints ?? []).some(
        (endPoint) =>
            endPoint.integrationId?.toLowerCase() === endpointId.toLowerCase(),
    );

export const useEmissionsConfigurations = () => {
    const service = useMemo(() => new EmissionsService(), []);
    const [endpoints, setEndpoints] = useState<
        OutboundMessagingConfiguration[]
    >([]);
    const [bodies, setBodies] = useState<EmissionsConfiguration[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        const controller = new AbortController();
        setIsLoading(true);
        setHasError(false);

        Promise.all([
            service.getEndpoints(controller.signal),
            service.getEmissionBodies(controller.signal),
        ])
            .then(([loadedEndpoints, loadedBodies]) => {
                if (controller.signal.aborted) return;
                setEndpoints(loadedEndpoints);
                setBodies(loadedBodies);
            })
            .catch(() => {
                if (!controller.signal.aborted) setHasError(true);
            })
            .finally(() => {
                if (!controller.signal.aborted) setIsLoading(false);
            });

        return () => controller.abort();
    }, [service, reloadKey]);

    const reload = useCallback(() => setReloadKey((key) => key + 1), []);

    return { service, endpoints, bodies, isLoading, hasError, reload };
};
